import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { paginatedSchema } from "../pagination";

import { baseAssetWithTimeseriesSchema } from "./assetsBase";
import { filterSchema } from "./filters";

export const groundwaterStationListItemSchema = baseAssetWithTimeseriesSchema.extend({
  filters: z.array(filterSchema),
  name: z.string(),
  station_type: z.string() /* Actually an enum */,
  status: z.string() /* Actually an enum */,
  scale: z.string() /* Actually an enum */,
  surface_level: z.number().nullable(),
  top_level: z.number().nullable(),
  bottom_level: z.number().nullable(),
});

export type GroundwaterStationListItem = z.infer<typeof groundwaterStationListItemSchema>;

export const groundwaterStationListSchema = paginatedSchema(groundwaterStationListItemSchema);
export type GroundwaterStationList = z.infer<typeof groundwaterStationListSchema>;

export const groundwaterStationSchema = groundwaterStationListItemSchema;
export type GroundwaterStation = z.infer<typeof groundwaterStationSchema>;

export function useGroundwaterStations(params = {}) {
  const url = combineUrlAndParams("/api/v4/groundwaterstations/", params);
  return useParsedQuery<GroundwaterStationList>(
    ["lizard-api-v4", "groundwaterstations", url],
    () => fetchWithError(url),
    (json: unknown) => groundwaterStationListSchema.parse(json)
  );
}

export function useGroundwaterStation(id: number) {
  return useParsedQuery<GroundwaterStation>(
    ["lizard-api-v4", "groundwaterstations", id],
    () => fetchWithError(`/api/v4/groundwaterstations/${id}/`),
    (json: unknown) => groundwaterStationSchema.parse(json)
  );
}

export function useMultipleGroundwaterStations(ids: number[]) {
  return useParsedQueries(
    ids.map((id) => {
      return {
        queryKey: ["lizard-api-v4", "groundwaterstations", id],
        fetchFunction: () => fetchWithError(`/api/v4/groundwaterstations/${id}/`),
        parseFunction: (json: unknown) => groundwaterStationSchema.parse(json),
      };
    })
  );
}
